import * as Sentry from "@sentry/react";

/**
 * Decoding of websocket frames (FE-11).
 *
 * The transport parses every payload ONCE, here, and never throws: a frame
 * that is not valid JSON is dropped and reported instead of reaching a hook's
 * `JSON.parse`, where one bad frame used to unmount the whole app.
 */

/** A decoded channel frame: `{ channel, data, account?, ... }`. */
export type RealtimeFrame = {
  channel?: string;
  account?: string;
  data?: any;
  [key: string]: any;
};

/** Enough to show a problem in Sentry without flooding it from one bad stream. */
export const MAX_FRAME_PARSE_REPORTS = 5;
/** Same budget for exceptions thrown while a hook handles a frame. */
export const MAX_FRAME_HANDLER_REPORTS = 5;
let reportsSent = 0;
let handlerReportsSent = 0;

/** Test hook: the report budgets are per page load. */
export const resetFrameParseReports = () => {
  reportsSent = 0;
  handlerReportsSent = 0;
};

const reportUnparsable = (raw: string, error: unknown) => {
  if (reportsSent >= MAX_FRAME_PARSE_REPORTS) return;
  reportsSent += 1;
  Sentry.captureException?.(error, {
    tags: { area: "realtime" },
    extra: {
      label: "Dropped an unparsable websocket frame",
      sample: raw.slice(0, 200),
    },
  });
};

/**
 * Run `handle` for every buffered frame, containing a throw to that one frame.
 *
 * Hooks fold frames inside effects; an exception there reaches the nearest
 * error boundary and unmounts the tree. One malformed frame (a `BigInt` of
 * garbage, a missing field) must cost only itself: the rest of the batch is
 * still applied, and Sentry hears about it at most MAX_FRAME_HANDLER_REPORTS
 * times per page load.
 */
export const forEachFrame = <T>(
  messages: readonly T[] | null | undefined,
  consumer: string,
  handle: (message: T, index: number) => void
) => {
  if (!messages) return;
  messages.forEach((message, index) => {
    try {
      handle(message, index);
    } catch (error) {
      if (handlerReportsSent >= MAX_FRAME_HANDLER_REPORTS) return;
      handlerReportsSent += 1;
      Sentry.captureException?.(error, {
        tags: { area: "realtime" },
        extra: { label: "Skipped a websocket frame its handler threw on", consumer },
      });
    }
  });
};

/**
 * Decode one socket payload without throwing. Strings are JSON-decoded,
 * anything else passes through unchanged; an unparsable string is `undefined`
 * (and reported, unless `reportInvalid` is false).
 */
export const parseSocketPayload = (
  raw: unknown,
  { reportInvalid = true }: { reportInvalid?: boolean } = {}
): unknown => {
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (error) {
    if (reportInvalid) reportUnparsable(raw, error);
    return undefined;
  }
};

/**
 * The frame a buffered message carries, or `undefined` when it holds no object.
 *
 * The transport now delivers parsed objects; legacy JSON strings (older test
 * fakes) are still accepted, parsed safely. Hooks read frames only through this.
 */
export const readFrame = <T extends object = RealtimeFrame>(
  message: { data?: unknown } | null | undefined
): T | undefined => {
  const value = parseSocketPayload(message?.data, { reportInvalid: false });
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as T)
    : undefined;
};
